/**
 * Login Route
 * Public route for user authentication with backend OAuth2 JWT APIs
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { LoginPage } from '../pages/Auth/LoginPage';
import { requireGuest } from '../router/guards';
import { useAuth } from '../hooks/useAuth';
import { LoginCredentials } from '../types/auth.types';

// Login route component with navigation and authentication handlers
const LoginRouteComponent = () => {
  const navigate = useNavigate();
  const { login, signInWithGoogle } = useAuth();

  // Google OAuth login handler - redirects to Google OAuth consent screen
  // After OAuth completes, user returns to /auth-callback which handles session
  const handleGoogleLogin = async () => {
    if (signInWithGoogle) {
      try {
        await signInWithGoogle();
        // Note: This code won't execute because signInWithGoogle redirects to Google
        // User will return to /auth-callback after OAuth completes
      } catch (error) {
        console.error('[Login] Google login failed:', error);
      }
    } else {
      console.log('[Login] Google OAuth not configured');
    }
  };

  const handleLogin = async (credentials: LoginCredentials) => {
    try {
      await login(credentials.email, credentials.password);
      // After successful login, navigate to home
      navigate({ to: '/home' });
    } catch (error) {
      console.error('[Login] Login failed:', error);
      // Error is handled by AuthContext and displayed in LoginPage
    }
  };

  return (
    <LoginPage
      onLogin={handleLogin}
      onForgotPassword={() => navigate({ to: '/forgot-password' })}
      onSignUp={() => navigate({ to: '/signup' })}
      onGoogleLogin={handleGoogleLogin}
    />
  );
};

// Create login route with guest guard
export const Route = createFileRoute('/login')({
  beforeLoad: requireGuest,
  component: LoginRouteComponent,
});
